import type { ImgHTMLAttributes } from "react";

/**
 * AVIF + WebP renditions generated by scripts/optimize-images.mjs. Imported
 * through Vite so every file is content-hashed into /assets (immutable cache).
 */
const files = import.meta.glob("../assets/photos/*.{avif,webp}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

interface Rendition {
  width: number;
  avif: string;
  webp: string;
}

const byName = new Map<string, Rendition[]>();
for (const [file, url] of Object.entries(files)) {
  const m = file.match(/\/([^/]+)-(\d+)\.(avif|webp)$/);
  if (!m) continue;
  const [, name, w, ext] = m;
  const list = byName.get(name) ?? [];
  let r = list.find((x) => x.width === +w);
  if (!r) list.push((r = { width: +w, avif: "", webp: "" }));
  r[ext as "avif" | "webp"] = url;
  byName.set(name, list.sort((a, b) => a.width - b.width));
}

/** Renditions for a photo, keyed by its source file's base name. */
function renditions(name: string): Rendition[] | undefined {
  return byName.get(name);
}

/** "/assets/images/service-painting.jpg" → "service-painting" */
const nameOf = (src: string) => src.split("/").pop()!.replace(/\.(jpe?g|png)$/i, "");

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  /** The photo's original path (also used elsewhere, e.g. as its og:image). */
  src: string;
  sizes: string;
  /** Lowercase on purpose: React 18 does not know the camelCase prop. */
  fetchpriority?: "high" | "low" | "auto";
};

/**
 * A photo as <picture>: AVIF first, WebP in the <img> for older browsers.
 * The <picture> is display:contents so the <img> keeps its exact layout
 * (absolute fills, object-fit, hover transforms) as if it stood alone.
 * Falls back to a plain <img> of the original if no renditions exist.
 */
const ResponsiveImage = ({ src, sizes, ...img }: Props) => {
  const set = renditions(nameOf(src));
  if (!set?.length) return <img src={src} sizes={sizes} {...img} />;
  const srcSet = (fmt: "avif" | "webp") => set.map((r) => `${r[fmt]} ${r.width}w`).join(", ");
  return (
    <picture className="contents">
      <source type="image/avif" srcSet={srcSet("avif")} sizes={sizes} />
      <img src={set[set.length - 1].webp} srcSet={srcSet("webp")} sizes={sizes} {...img} />
    </picture>
  );
};

export default ResponsiveImage;
